/*
  Mobile Slider and Zoom Functionality
  Initialized on the DOMContentLoaded event, this provides the following features 
  for elements with the 'data-hero-gallery' attribute:
  
  1. If screen width is 749px or less, initializes a mobile slider using the Swiper library.
  2. Displays one slide at a time with a pagination fraction.
  3. Implements a 1.5x zoom effect when hovering over images.
  4. Dynamically updates the zoom focal point based on the mouse position.
  5. Re-initializes functionality on window resize (includes debouncing).
*/

/*
  Product Details Drawer
*/
const initProductDetailsDrawer = () => {
  const drawerOpenerList = document.querySelectorAll(
    "[data-product-details-drawer-opener]"
  );
  const drawer = document.querySelector("#product-details-drawer");

  if (!drawer || !drawerOpenerList.length) return;

  let currentActiveButton = null;
  let isClickScrolling = false;
  let clickScrollTimeout = null;

  // Handle scrolling within the drawer
  const scrollToSection = (sectionId) => {
    const targetSection = document.getElementById(sectionId);
    if (!targetSection) return;

    setTimeout(() => {
      aside.scroll({
        top: targetSection.offsetTop,
        behavior: "smooth",
      });
      if(targetSection.querySelector(".accordion").getAttribute("aria-expanded") === "false"){
        targetSection.querySelector(".accordion").click();
      }
    }, 300);
  };

  // Open drawer
  drawerOpenerList.forEach((opener) => {
    opener.addEventListener("click", (e) => {
      e.preventDefault();
      drawer.classList.add("is-active");
      document.body.classList.add("no-scroll");
    });
  });

  // Close drawer
  const closeBtnList = drawer.querySelectorAll(
    ".product-details-drawer__close-button,.product-details-drawer__bg"
  );
  const closeDrawer = () => {
    drawer.classList.remove("is-active");
    document.body.classList.remove("no-scroll");
  };

  closeBtnList.forEach((closeBtn) => {
    closeBtn.addEventListener("click", closeDrawer);
  });

  const aside = drawer.querySelector(".product-details-drawer__aside");
  const sections = drawer.querySelectorAll("section[id]");

  const observerOptions = {
    root: aside,
    threshold: 0,
    rootMargin: "-10px 0px -90% 0px",
  };

  const observerCallback = (entries) => {
    if (isClickScrolling) return;
    entries.forEach((entry) => {
      // Set the last section whose top enters the display area to active
      if (entry.isIntersecting) {
        const sectionId = entry.target.id;
        const menuButton = drawer.querySelector(
          `.product-details-drawer__menu-button[data-target="${sectionId}"]`
        );

        if (currentActiveButton && currentActiveButton !== menuButton) {
          currentActiveButton.classList.remove("active");
        }

        menuButton.classList.add("active");
        currentActiveButton = menuButton;
      }
    });
  };

  const observer = new IntersectionObserver(observerCallback, observerOptions);
  sections.forEach((section) => observer.observe(section));


  const menuButtons = drawer.querySelectorAll(
    ".product-details-drawer__menu-button[data-target]"
  );
  const endClickScroll = () => {
    isClickScrolling = false;
    aside.removeEventListener("scrollend", endClickScroll);
  };

  menuButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const sectionId = button.getAttribute("data-target");
      if (currentActiveButton && currentActiveButton !== button) {
        currentActiveButton.classList.remove("active");
      }
      button.classList.add("active");
      currentActiveButton = button;

      isClickScrolling = true;
      clearTimeout(clickScrollTimeout);
      aside.removeEventListener("scrollend", endClickScroll);
      aside.addEventListener("scrollend", endClickScroll, { once: true });
      // Fallback for browsers without scrollend support
      clickScrollTimeout = setTimeout(endClickScroll, 1200);

      scrollToSection(sectionId, button);
    });
  });

  // Accordion display logic
  const accordionButtons = drawer.querySelectorAll(".accordion");
  accordionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const activeSection = button.closest(
        ".product-details-drawer__aside-section"
      );
      const preActiveButton = drawer.querySelector(
        '.accordion[aria-expanded="true"]'
      );
      const preActiveSection = drawer.querySelector(
        ".product-details-drawer__aside-section.active"
      );

      if (preActiveSection?.id === activeSection?.id) {
        button.setAttribute("aria-expanded", false);
        activeSection.classList.remove("active");
      } else {
        if (preActiveButton) {
          preActiveButton.setAttribute("aria-expanded", false);
        }
        if (preActiveSection) {
          preActiveSection.classList.remove("active");
        }

        const isExpanded = button.getAttribute("aria-expanded") === "true";

        button.setAttribute("aria-expanded", !isExpanded);
        if (!isExpanded) {
          button
            .closest(".product-details-drawer__aside-section")
            .classList.add("active");
        } else {
          button
            .closest(".product-details-drawer__aside-section")
            .classList.remove("active");
        }
      }
    });
  });
};
document.addEventListener("DOMContentLoaded", initProductDetailsDrawer);

/*
  Judge.me Review Form — Close drawer on "Write a review"

  Judge.me injects the review form modal inside the widget, which on PDPs lives inside
  #product-details-drawer (position: fixed; z-index: 9999). That ancestor creates a
  stacking context the modal cannot escape, so the form opens but stays trapped behind
  the drawer — looks unresponsive until the drawer is closed.

  Pragmatic fix: when the user clicks the Judge.me "Write a review" trigger inside the
  drawer, close the drawer first. The form then opens at its normal body-level stack and
  is fully interactive.

  Uses event delegation so it works for buttons that Judge.me re-renders dynamically.
*/
const initJudgemeReviewClose = () => {
  const drawer = document.querySelector("#product-details-drawer");
  if (!drawer) return;

  const TRIGGER_SELECTOR = [
    ".jdgm-write-rev-link",
    ".jdgm-rev-widg__write-rev-link",
    "[data-action='write-review']"
  ].join(",");

  drawer.addEventListener("click", (e) => {
    const trigger = e.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    drawer.classList.remove("is-active");
    document.body.classList.remove("no-scroll");
  });
};
document.addEventListener("DOMContentLoaded", initJudgemeReviewClose);

/*
  Size Chart Drawer
*/
const initSizeChartPopup = () => {
  const popupOpenerList = document.querySelectorAll(
    "[data-size-chart-popup-opener]"
  );
  const popup = document.querySelector("#product-size-chart-popup");

  if (!popup || !popupOpenerList.length) return;

  // Open drawer/popup
  opener.addEventListener("click", (e) => {
    if (e.target.closest('a[href]')) return; 
    e.preventDefault();
    popup.classList.add("is-active");
    document.body.classList.add("no-scroll");
  });

  // Close drawer/popup
  const closeBtnList = popup.querySelectorAll(
    ".product-size-chart-popup__close-button,.product-size-chart-popup__bg"
  );
  const closePopup = () => {
    popup.classList.remove("is-active");
    document.body.classList.remove("no-scroll");
  };

  closeBtnList.forEach((closeBtn) => {
    closeBtn.addEventListener("click", closePopup);
  });

  // Calculate and set popup header height for CSS variables
  const calcPopupHeaderHeight = () => {
    const popupHeaderHeight = popup.querySelector(
      ".product-size-chart-popup__header"
    ).offsetHeight;

    popup.style.setProperty("--popup-header-height", `${popupHeaderHeight}px`);
  };
  calcPopupHeaderHeight();

  window.addEventListener("resize", () => {
    calcPopupHeaderHeight();
  });
};
document.addEventListener("DOMContentLoaded", initSizeChartPopup);

document.addEventListener('click', function(event) {
  const isFitButton = event.target.closest('.size-chart-opener'); 
  const isSpecsLink = event.target.closest('.product-features__chart-link');

  if (isSpecsLink && isSpecsLink.tagName === 'a') return;

  if (isFitButton || isSpecsLink) {
    event.preventDefault();
    
    const sizeChartDrawer = document.querySelector('x-drawer[id^="size-chart"]');
    
    if (sizeChartDrawer) {
      if (typeof sizeChartDrawer.show === 'function') {
        sizeChartDrawer.show();
      } else {
        sizeChartDrawer.setAttribute('open', '');
      }
    } else {
      console.error("Size chart drawer not found in DOM.");
    }
  }
});

// JudgeMe (review) section
(function() {
  const handleReviewClick = function(event) {

    const judgeMeBadge = event.target.closest('.jdgm-preview-badge');
    if (!judgeMeBadge) return;

    event.preventDefault();
    event.stopPropagation();

    const drawer = document.getElementById('product-details-drawer');
    const aside = drawer ? drawer.querySelector(".product-details-drawer__aside") : null;

    if (drawer) {
      drawer.classList.add("is-active");
      document.body.classList.add("no-scroll");

      const targetSection = document.getElementById('reviews');
      if (targetSection && aside) {
        setTimeout(() => {
          aside.scroll({
            top: targetSection.offsetTop,
            behavior: "smooth",
          });
          
          const accordionBtn = targetSection.querySelector(".accordion");
          if (accordionBtn && accordionBtn.getAttribute("aria-expanded") === "false") {
            accordionBtn.click();
          }
        }, 400); 
      } else {
        console.error("Could not find #reviews or drawer aside.");
      }
    } else {
      console.error("Could not find #product-details-drawer.");
    }
  };

  document.addEventListener('click', handleReviewClick, true);
  
})();